// A user's own Artemis skills (WEB-7124, API-3688): how they hunt, saved and followed on later
// questions. A Skill is { Id, Name, Title, AppliesWhen, Body, Enabled, Version, CreatedFrom?, BasedOn? }.

export const DEFAULT_LIMITS = { MaxEnabled: 20, MaxSkills: 50, MaxNameLength: 60, MaxTitleLength: 80, MaxAppliesWhenLength: 300, MaxBodyLength: 4000 }

export const capitalise = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '')

// "Late-season elk glassing!" → "late-season-elk-glassing": the Name the server keys a skill by.
export const skillNameFrom = (title, maxLength = DEFAULT_LIMITS.MaxNameLength) => String(title || '')
  .toLowerCase()
  .normalize('NFKD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, maxLength)
  .replace(/-+$/, '')

// Problems with a skill form, by field, or {} when it can be saved.
export const skillFormErrors = (form, limits = DEFAULT_LIMITS) => {
  const errors = {}
  const title = (form.title || '').trim(), when = (form.appliesWhen || '').trim(), body = (form.body || '').trim()
  if (!title) errors.title = 'Give it a name.'
  else if (title.length > limits.MaxTitleLength) errors.title = `At most ${limits.MaxTitleLength} characters.`
  else if (!skillNameFrom(title, limits.MaxNameLength)) errors.title = 'Use some letters or numbers.'
  if (!when) errors.appliesWhen = 'Say when Artemis should use it.'
  else if (when.length > limits.MaxAppliesWhenLength) errors.appliesWhen = `At most ${limits.MaxAppliesWhenLength} characters.`
  if (!body) errors.body = 'Describe how you hunt.'
  else if (body.length > limits.MaxBodyLength) errors.body = `At most ${limits.MaxBodyLength.toLocaleString('en-US')} characters.`
  return errors
}

// A form's values as the POST body (new) or the PATCH of what changed (existing).
export const skillPayload = (form, existing = null, limits = DEFAULT_LIMITS) => {
  const fields = {
    Title: form.title.trim(),
    AppliesWhen: form.appliesWhen.trim(),
    Body: form.body.trim(),
    Enabled: !!form.enabled
  }
  if (!existing) {
    return { Name: skillNameFrom(fields.Title, limits.MaxNameLength), ...fields, ...(form.basedOn ? { BasedOn: form.basedOn } : {}) }
  }
  const patch = {}
  Object.keys(fields).forEach(k => { if (fields[k] !== existing[k]) patch[k] = fields[k] })
  return patch
}

export const emptySkillForm = () => ({ title: '', appliesWhen: '', body: '', enabled: true, basedOn: null })
export const formFromSkill = skill => ({ title: skill.Title || '', appliesWhen: skill.AppliesWhen || '', body: skill.Body || '', enabled: skill.Enabled !== false, basedOn: skill.BasedOn || null })

// A create_skill / update_skill step's Result as a Skill, or null.
export const savedSkillFrom = result => {
  if (!result || result.Kind !== 'user_skill' || result.Id == null) return null
  const { Kind, Action, ...skill } = result
  return { action: Action === 'updated' ? 'updated' : 'created', skill }
}

// "3 of 20 on" for the skills list.
export const enabledLabel = (skills, limits = DEFAULT_LIMITS) =>
  `${skills.filter(s => s.Enabled !== false).length} of ${limits.MaxEnabled} on`

// A preview of a skill's text: its first ~200 characters, ending on a word.
export const bodyPreview = (body, max = 200) => {
  const text = String(body || '').replace(/\s+/g, ' ').trim()
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), max - 20))}…`
}

// Optimized skills (API-3801): Artemis rewrites each saved skill for Claude in the background,
// and refuses a text that isn't a hunting method. The user's own Body and AppliesWhen are still
// what the app shows and edits; the rewrite is read only.

// True when Artemis won't use the skill as written, until it's edited.
export const isRefused = skill => !!(skill && skill.Refused)

// How Artemis reads a skill: { body, appliesWhen, tools }, or null when there's no rewrite to show.
export const skillReading = skill => {
  if (!skill || !skill.Optimized || skill.Refused) return null
  const body = String(skill.OptimizedBody || '').trim()
  const appliesWhen = String(skill.OptimizedAppliesWhen || '').trim()
  const tools = Array.isArray(skill.OptimizedTools)
    ? [...new Set(skill.OptimizedTools.filter(t => typeof t === 'string' && t.trim()).map(t => t.trim()))]
    : []
  if (!body && !appliesWhen && tools.length === 0) return null
  return { body, appliesWhen, tools }
}

// True when a save starts a rewrite: a new skill, or a change to its Body or AppliesWhen.
export const startsRewrite = (payload, existing = null) =>
  !existing || !!payload && ('Body' in payload || 'AppliesWhen' in payload)

// True while a skill may still be rewritten: it's not optimized yet and wasn't refused.
export const awaitingRewrite = skill => !!skill && !skill.Optimized && !skill.Refused

// After a save the rewrite usually lands within a few seconds: look again then, and once more
// later only if it hasn't (see useUserSkills).
export const REWRITE_REFRESH_DELAYS = [4000, 12000]

// -- the hub's Skills section (WEB-7170) ---------------------------------------------------------------

// The built-in a skill was copied from, by CreatedFrom ("copy:<name>") or BasedOn; null otherwise.
export const copiedFrom = skill => {
  if (!skill) return null
  const m = /^copy:(.+)$/.exec(skill.CreatedFrom || '')
  return m ? m[1] : (skill.BasedOn || null)
}

// The line under a skill in the list: refused, "Based on Artemis's …", or where it came from and its version.
// builtIns: GET Skills/BuiltIn, for the method's title.
export const skillNote = (skill, builtIns = []) => {
  if (isRefused(skill)) return 'Refused · not a hunting method'
  const base = copiedFrom(skill)
  const version = Number(skill.Version) > 0 ? `v${skill.Version}` : null
  if (base) {
    const method = builtIns.find(b => b.Name === base)
    return [`Based on Artemis’s ${capitalise((method && method.Title) || base.replace(/-/g, ' '))}`, version].filter(Boolean).join(' · ')
  }
  return [skill.CreatedFrom === 'chat' ? 'Saved from a chat' : null, version].filter(Boolean).join(' · ')
}

// Whether the user already has a copy of a built-in method (it then shows "Added").
export const hasCopyOf = (skills, name) => (skills || []).some(s => copiedFrom(s) === name)

// A new skill's form from a built-in (GET Skills/BuiltIn/{name}): its title, its description as when to
// use it, and its text, with BasedOn recording where it came from.
export const draftFromBuiltIn = builtIn => ({
  title: capitalise(builtIn.Title || String(builtIn.Name || '').replace(/-/g, ' ')),
  appliesWhen: builtIn.Description || '',
  body: builtIn.Body || '',
  enabled: true,
  basedOn: builtIn.Name || null
})

// A built-in's line: its species and what it's for.
export const methodLine = builtIn => [
  Array.isArray(builtIn.Species) ? builtIn.Species.map(capitalise).join(', ') : builtIn.Species ? capitalise(builtIn.Species) : null,
  builtIn.Description
].filter(Boolean).join(' · ')

// What "How Artemis reads it" says: 'refused', 'ready' (the rewrite is there), 'rewriting' (saved in the
// last REWRITING_MS and not rewritten yet), 'own' (Artemis follows the user's words until it is), or null
// for a skill not saved yet.
export const REWRITING_MS = 20000
export const rewriteState = (skill, savedAt = null, now = Date.now()) => {
  if (!skill || skill.Id == null) return null
  if (isRefused(skill)) return 'refused'
  if (skillReading(skill)) return 'ready'
  return savedAt && now - savedAt < REWRITING_MS ? 'rewriting' : 'own'
}
